import { BusinessLogicError, ErrorType } from '@40seas/errors';
import { RumInitConfiguration, datadogRum } from '@datadog/browser-rum';

const DATADOG_INTAKE_PATTERNS: RegExp[] = [
    /https:\/\/.*browser-intake.*datadoghq\.com/i,
    /https:\/\/.*browser-intake.*datadoghq\.eu/i,
    /https:\/\/.*browser-intake.*datadoghq\.us/i,
    /https:\/\/.*browser-intake.*ddog-gov\.com/i,
    /https:\/\/.*rum-http-intake\.logs\.datadoghq\.com/i,
    /https:\/\/.*rum-http-intake\.logs\.datadoghq\.eu/i,
    /https:\/\/.*datad0g\./i,
];

const THIRD_PARTY_ANALYTICS_PATTERNS: RegExp[] = [/https:\/\/.*mixpanel\.com/i, /https:\/\/api-js\.mixpanel\.com/i];

function matchesIntake(url?: string): boolean {
    if (!url) return false;
    return DATADOG_INTAKE_PATTERNS.some((rx) => rx.test(url));
}

function matchesThirdPartyAnalytics(url?: string): boolean {
    if (!url) return false;
    return THIRD_PARTY_ANALYTICS_PATTERNS.some((rx) => rx.test(url));
}

function shouldSuppressError(event: any): boolean {
    const url = event?.error?.resource?.url as string | undefined;
    const httpUrl = (event as any)?.http?.url as string | undefined;
    const statusCode = event?.error?.resource?.statusCode || (event as any)?.http?.status_code;
    const message = (event?.error?.message as string | undefined) || '';
    const stack = (event?.error?.stack as string | undefined) || '';
    const combinedText = `${message} ${stack} ${url || ''} ${httpUrl || ''}`;

    // Don't report 404 errors - these are expected client errors, not application bugs
    if (statusCode === 404 || /404|not found/i.test(combinedText)) {
        return true;
    }

    // Don't report network errors - these are infrastructure issues, not application bugs
    if (message === 'A network error has occurred. Please try again.') {
        return true;
    }

    // Don't report generic "Failed to fetch" errors
    // Can be caused by: network disconnection, DNS failure, CORS, firewall blocks, etc.
    if (/Failed to fetch/i.test(message)) {
        return true;
    }

    // Don't report Datadog's own intake errors
    if (matchesIntake(url) || matchesIntake(httpUrl)) {
        return true;
    }

    // Don't report status code 0 errors - these are network/firewall blocks
    // Status 0 means the request never completed (blocked by firewall, VPN, DNS failure, etc.)
    // These are infrastructure issues, not actionable application errors
    if (statusCode === 0 || /status:\s*0|Bad HTTP status:\s*0/i.test(combinedText)) {
        return true;
    }

    // Don't report third-party analytics failures (Mixpanel, etc.)
    if (matchesThirdPartyAnalytics(url) || matchesThirdPartyAnalytics(httpUrl)) {
        return true;
    }

    // Don't report errors that contain "mixpanel" in the message/stack
    if (/mixpanel/i.test(combinedText)) {
        return true;
    }

    // Don't report Datadog's own transport errors (to avoid recursive error reporting)
    if (
        /(sendWithRetryStrategy|fetchObservable|httpRequest|instrumentMethod|transport\/|browser-intake)/i.test(combinedText) &&
        /datadoghq|ddog-gov/i.test(combinedText)
    ) {
        return true;
    }

    // Don't report "Loading chunk failed" errors (temporary workaround)
    if (/Loading chunk \d+ failed|ChunkLoadError/i.test(combinedText)) {
        return true;
    }

    // Don't report ResizeObserver loop errors - these are benign browser warnings
    // that occur when resize callbacks trigger layout changes, commonly from PDF viewers
    if (/ResizeObserver loop completed with undelivered notifications/i.test(message)) {
        return true;
    }

    // Don't report DOM 'removeChild' errors - these are benign React/DOM race conditions
    // that occur when React tries to remove a node that has already been removed
    if (/Failed to execute 'removeChild' on 'Node'/i.test(message)) {
        return true;
    }

    // Don't report "Invalid token" errors - these are expected user-facing errors
    // that occur when a user accesses an expired or invalid checkout/payment link
    if (/Invalid token, please contact your supplier/i.test(message)) {
        return true;
    }

    // Don't report "Timeout when executing 'fetch'" errors from MessagePort/worker contexts
    // These are typically from the Datadog SDK's internal transport layer timing out,
    // not from first-party API calls. The MessagePort stack trace indicates this is
    // an internal SDK worker communication timeout, not an actionable application error.
    if (/Timeout when executing 'fetch'/i.test(message) && /MessagePort/i.test(stack)) {
        return true;
    }

    return false;
}

class DatadogRumProvider {
    private shouldReport() {
        const isLocalhost = typeof window !== 'undefined' && window?.location?.hostname === 'localhost';
        const isPrp = (window as any)?.__SETUP__?.HOSTNAME;
        const isLocalAssets = process.env.APP_VERSION === 'local-version';

        return !isLocalhost && !isPrp && !isLocalAssets;
    }

    getErrorMessage = (error: any) => {
        return error?.response?.data?.message || error?.message || error?.toString() || 'An error occurred';
    };

    initDatadogRum = (
        configuration: Pick<RumInitConfiguration, 'clientToken' | 'applicationId' | 'service'> & Partial<RumInitConfiguration>,
    ) => {
        if (this.shouldReport()) {
            datadogRum.init({
                site: 'datadoghq.com',
                sessionSampleRate: 100,
                sessionReplaySampleRate: 100,
                trackUserInteractions: true,
                trackResources: true,
                version: process.env.APP_VERSION,
                trackLongTasks: true,
                allowedTracingUrls: [/https:\/\/api.40seas\.com\/.*/, /https:\/\/dashboard.40seas\.com\/.*/],
                enableExperimentalFeatures: ['feature_flags'],
                beforeSend: (event: any) => {
                    if (event?.type === 'resource' && matchesIntake(event?.resource?.url)) {
                        return false;
                    }

                    if (event?.type === 'error' && shouldSuppressError(event)) {
                        return false;
                    }

                    return true;
                },
                excludedActivityUrls: DATADOG_INTAKE_PATTERNS,
                ...configuration,
                // Use 'mask-user-input' to prevent recording sensitive form inputs (passwords, etc.) in session replays
                // This MUST come after ...configuration to ensure it cannot be overridden
                defaultPrivacyLevel: 'mask-user-input',
            });
            datadogRum.startSessionReplayRecording();
        }
    };

    getSessionId = () => {
        return datadogRum.getInternalContext()?.session_id;
    };

    addFeatureFlagEvaluation = (key: string, detail: { value: string | number | boolean }) => {
        if (this.shouldReport()) {
            // it is an experimental feature, so we need to ignore the type
            // @ts-ignore
            datadogRum.addFeatureFlagEvaluation(key?.replace('-', '_'), detail.value);
        }
    };

    setUser = (user: Record<string, string | number>) => {
        if (this.shouldReport()) {
            datadogRum.setUser(user);
        }
    };

    setGlobalContext = (key: string, property: any) => {
        if (this.shouldReport()) {
            datadogRum.setGlobalContextProperty(key, property);
        }
    };

    updateGlobalContext = (key: string, property: any) => {
        if (this.shouldReport()) {
            const context = datadogRum.getGlobalContext()[key] as {};
            datadogRum.setGlobalContextProperty(key, {
                ...context,
                ...property,
            });
        }
    };

    removeGlobalContext = (key: string) => {
        if (this.shouldReport()) {
            datadogRum.removeGlobalContextProperty(key);
        }
    };

    getVersion() {
        return process.env.APP_VERSION as string;
    }

    reportToDatadog = (error: any, context?: any) => {
        if (error && typeof error === 'object' && this.shouldReport()) {
            if (
                error instanceof BusinessLogicError ||
                error.response?.data?.type === ErrorType.BusinessLogic ||
                error.response?.data?.type === ErrorType.Validation
            ) {
                return;
            }

            // Don't report "Invalid token" errors - these are expected user-facing errors
            // that occur when a user accesses an expired or invalid checkout/payment link
            if (/Invalid token, please contact your supplier/i.test(this.getErrorMessage(error))) {
                return;
            }

            // datadog doesn't support reporting errors without stack trace
            // more info at https://docs.datadoghq.com/real_user_monitoring/browser/collecting_browser_errors
            if (!error.stack) {
                const message = this.getErrorMessage(error);
                try {
                    console.error(message);
                } catch (e) {
                    console.error('Failed to log error message', e);
                }
                datadogRum.addError(new Error(message), context);

                return;
            }

            datadogRum.addError(error, context);
        }
    };
}

export const datadogRumProvider = new DatadogRumProvider();
